/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: asset-validation-service.ts
 *
 * Created by randysorensen on 8/17/18.
 */

angular.module('prism-ui.components.data').service(
'AssetValidationService',
function (
    this: JS5Object,
    $q: JS5Object,
    _: JS5Object,
    MediaInfoService: JS5Object,
    MediaProfileResourceFactory: JS5Object,
    PackageResourceFactory: JS5Object
) {
    const parseValidationFailures = (validationResults: JS5Object, assetPath: string, acceptProfileId: string) => {
        let validationMessageList: JS5Object = {
            source: assetPath,
            errors: <JS5Object[]> [],
            warnings: <JS5Object[]> []
        };

        if (_.has(validationResults, acceptProfileId)) {

            //
            // Some validation result objects are keyed by e.g.
            // the accept profile id; others are simply arrays of
            // result objects.
            //

            validationResults = validationResults[acceptProfileId].failures;
        } else if (_.has(validationResults, 'failures')) {

            //
            // And, some validation result objects simply have a
            // property named "failures".
            //

            validationResults = validationResults.failures;
        }

        if (validationResults.length) {
            _.forEach(validationResults, (value: JS5Object) => {
                if (value.property && value.actualValue && value.expectedValue) {
                    value.description =
                        `${value.property}: Actual value ${value.actualValue}; ` +
                        `expected value ${value.expectedValue}`;
                } else if (value.source) {
                    value.description = `${value.source}: ${value.description}`;
                }

                if (value.severity === 'WARNING') {
                    validationMessageList.warnings.push(value);
                } else {
                    validationMessageList.errors.push(value);
                }
            });
        }

        return validationMessageList;
    };


    //==================//
    // Media Validation //
    //==================//

    this.validateMediaLocal = (localMediaFile: File, mediaAcceptProfile: JS5Object) => {
        return MediaInfoService.executeMediaInfo(localMediaFile)
            .then((mediaInfoXml: string) => {
                let queryParams = {
                    profileId: mediaAcceptProfile.id
                };
                return MediaProfileResourceFactory.validateXML(queryParams, mediaInfoXml).$promise
                    .then((validationResults: JS5Object) => {
                        let result = parseValidationFailures(validationResults, localMediaFile.name, mediaAcceptProfile.id);
                        result.mediaInfo = mediaInfoXml;
                        return result;
                    });
            });
    };

    this.validateMediaRemote = (virtualPath: string, mediaAcceptProfileId: string) => {
        let queryParams = {
            profileId: mediaAcceptProfileId,
            path: virtualPath
        };

        return MediaProfileResourceFactory.validate(queryParams).$promise
            .then((validationResults: JS5Object) => {
                return parseValidationFailures(validationResults, virtualPath, mediaAcceptProfileId);
            });
    };


    //=================//
    // Text Validation //
    //=================//

    const readFile = (textFile: File) => {
        let onloadDeferred = $q.defer();
        let fileReader = new FileReader();
        fileReader.onload = () => {
            onloadDeferred.resolve(fileReader.result);
        };
        fileReader.readAsDataURL(textFile);
        return onloadDeferred.promise;
    };

    this.validateTextLocalMainVideoLocal = (
        textFile: File,
        textAcceptTemplate: JS5Object,
        mainVideoAcceptTemplateMember: JS5Object,
        videoMediaInfoXML: string,
    ) => {
        return readFile(textFile)
            .then((readerResult: any) => {
                let timedTextValidationDTO: JS5Object = {
                    timedTextFilename: textFile.name,
                    timedTextData: readerResult,
                    videoAcceptTemplateMemberId: mainVideoAcceptTemplateMember.id,
                    mainVideoInfoXML: videoMediaInfoXML
                };

                if (textAcceptTemplate) {
                    timedTextValidationDTO.textAcceptTemplateId = textAcceptTemplate.id;
                }

                return MediaProfileResourceFactory.validateCCSubtitle({}, timedTextValidationDTO)
                    .$promise.then((validationResults: JS5Object[]) => {
                    return parseValidationFailures(validationResults, textFile.name,
                        _.get(textAcceptTemplate, 'id'));
                });
            });
    };

    this.validateTextLocalMainVideoRemote = (
        textFile: File,
        textAcceptTemplate: JS5Object,
        mainVideoAcceptTemplateMember: JS5Object,
        video: JS5Object
    ) => {
        return readFile(textFile)
            .then((readerResult: any) => {
                let timedTextValidationDTO: JS5Object = {
                    timedTextFilename: textFile.name,
                    timedTextData: readerResult,
                    videoAcceptTemplateMemberId: mainVideoAcceptTemplateMember.id,
                    mainVideoId: video.id
                };

                if (textAcceptTemplate) {
                    timedTextValidationDTO.textAcceptTemplateId = textAcceptTemplate.id;
                }

                return MediaProfileResourceFactory.validateCCSubtitle({}, timedTextValidationDTO)
                    .$promise.then((validationResults: JS5Object[]) => {
                    return parseValidationFailures(validationResults, textFile.name,
                        _.get(textAcceptTemplate, 'id'));
                });
            });
    };

    this.validateTextRemoteMainVideoRemote = (
        virtualPath: string,
        textAcceptTemplate: JS5Object,
        video: JS5Object
    ) => {
        const textAcceptTemplateId = _.get(textAcceptTemplate, 'id', '');
        const videoId = _.get(video, `id`, null);
        const videoPath = _.get(video, `virtualPath`, null);
        const videoIngestProgress = _.get(video, `ingestInfo.progress`, 0);
        const videoAcceptTemplateMember = video.acceptTemplateMember;

        let queryParams: JS5Object = {
            ccPath: virtualPath,
            videoAcceptTemplateMemberId: videoAcceptTemplateMember.id,
            textAcceptTemplateId: textAcceptTemplateId,
        };

        if (videoId && parseInt(videoIngestProgress) === 100) {
            queryParams.videoMediaId = videoId;
        } else {
            queryParams.videoPath = videoPath;
        }

        let validationPromise = MediaProfileResourceFactory.validateClosedCaption(queryParams).$promise;
        return validationPromise.then((validationResults: JS5Object[]) => {
            return parseValidationFailures(validationResults, virtualPath, textAcceptTemplateId);
        });
    };


    //============================//
    // Component Video Validation //
    //============================//

    this.validateComponentsLocal = (
        packageId: number,
        mediaInfoDTOList: JS5Object[],
        newVideoComponentFile: File,
        mediaAcceptProfile: JS5Object,
        componentRoleId: number,
        excludedComponentVideoIds: number[],
        mainVideoMediaInfoDTO: JS5Object
    ) => {
        return MediaInfoService.executeMediaInfo(newVideoComponentFile)
            .then((newVideoComponentMediaInfoXml: string) => {
                let videoComponentValidationDTO: JS5Object = {
                    packageId,
                    profileId: mediaAcceptProfile.id,
                    roleId: componentRoleId,
                    videoComponents: <JS5Object>[],
                    excludedContentIds: excludedComponentVideoIds
                };

                if (mainVideoMediaInfoDTO) {
                    videoComponentValidationDTO.mainMediaInfo = mainVideoMediaInfoDTO;
                }

                _.forEach(mediaInfoDTOList, (mediaInfoDTO: JS5Object) => {
                    videoComponentValidationDTO.videoComponents.push(mediaInfoDTO);
                });

                videoComponentValidationDTO.videoComponents.push({
                    filePath: newVideoComponentFile.name,
                    mediaInfo: newVideoComponentMediaInfoXml
                });

                return MediaProfileResourceFactory
                    .validateVideoComponents({}, videoComponentValidationDTO).$promise
                    .then((validationResults: JS5Object[]) => {
                        let results: JS5Object[] = [];

                        _.forEach(validationResults, (validationResult: JS5Object, assetPath: string) => {
                            if (_.has(validationResult, 'failures')) {
                                let parsedValidationResult =
                                    parseValidationFailures(validationResult.failures, assetPath, mediaAcceptProfile.id);

                                results.push(parsedValidationResult);
                            }
                        });
                        if (_.isEmpty(results)) {
                            results.push({
                                source: 'Component Videos',
                                errors: [],
                                warnings: [],
                            });
                        }
                        results[0].uploadedData = {
                            mediaInfoDTO: {
                                filePath: newVideoComponentFile.name,
                                mediaInfo: newVideoComponentMediaInfoXml,
                            },
                        };

                        return results;
                    });
            });
    };

    this.validateComponentsRemoteMainVideoRemote = (packageId: number, componentVirtualPaths: string[],
                                                    mediaAcceptProfileId: string, componentRoleId: number,
                                                    excludedComponentVideoIds: number[]
    ) => {
        let queryParams = {
            id: packageId,
            path: componentVirtualPaths,
            profileId: mediaAcceptProfileId,
            roleId: componentRoleId,
            sub: 'videoComponentValidationResult',
            excludeContentId: excludedComponentVideoIds
        };

        return PackageResourceFactory.get(queryParams).$promise.then((validationResults: JS5Object) => {
            let results: JS5Object[] = [];

            _.forEach(validationResults, (validationResult: JS5Object, assetPath: string) => {
                if (_.has(validationResult, 'failures')) {
                    let parsedValidationResult =
                        parseValidationFailures(validationResult.failures, assetPath, mediaAcceptProfileId);

                    results.push(parsedValidationResult);
                }
            });
            if (_.isEmpty(results)) {
                results.push({
                    source: 'Component Videos',
                    errors: [],
                    warnings: [],
                });
            }

            return results;
        });
    };


    //==================//
    // AdPod Validation //
    //==================//

    this.validateAdPodLocal = (adPodFile: File, adPodAcceptProfile: JS5Object) => {
        return readFile(adPodFile)
            .then((readerResult: any) => {
                let adPodFilename = adPodFile.name;
                let adPodAcceptProfileId = adPodAcceptProfile.id;

                const adPodValidationDTO = {
                    adPodProfileId: adPodAcceptProfileId,
                    adPodFilename: adPodFilename,
                    adPodData: readerResult
                };

                return MediaProfileResourceFactory.validateAdPod({}, adPodValidationDTO)
                    .$promise.then((validationResults: JS5Object[]) => {
                    return parseValidationFailures(validationResults, adPodFilename,
                        adPodAcceptProfileId);
                });
            });
    };

});
